import {useEffect, useState} from "react";

import SwitchView from "@/components/SwitchView";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";

import {REQUIRED_FIELD, rpeOptions} from "@/utils/constants";
import {checkRequiredFields, playerToOption} from "@/utils/functions";

import "./RpeTab.scss";
import "./handleRPE.scss";

// Fascia di carico (come in RpeTab): classe rpe-tone--*
const rpeTone = (v) => {
	const n = parseInt(v);
	if (!n) return "rpe-tone--none";
	if (n <= 3) return "rpe-tone--low";
	if (n <= 6) return "rpe-tone--moderate";
	if (n <= 8) return "rpe-tone--hard";
	return "rpe-tone--vhard";
};

// ── Inline 1-10 button selector ───────────────────────────────────────────────

function RpeSelector({value, onChange}) {
	// value can be a raw string "7" or an option object {value:"7", label:"..."}
	const numVal =
		typeof value === "object" ? parseInt(value?.value) : parseInt(value);

	return (
		<div className="rpe-sel">
			{Array.from({length: 10}, (_, i) => i + 1).map((n) => {
				const active = numVal === n;
				return (
					<button
						key={n}
						type="button"
						className={`rpe-sel__btn ${rpeTone(n)}${active ? " rpe-sel__btn--active" : ""}`}
						onClick={() => onChange(String(n))}
						title={rpeOptions.find((o) => o.value === String(n))?.label}
					>
						{n}
					</button>
				);
			})}
		</div>
	);
}

// ── Main component ────────────────────────────────────────────────────────────

function HandleRPEModal({
	open,
	confirm,
	close,
	initialData,
	players,
	rosterPlayerIds,
}) {
	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState([]);
	const [insertType, setInsertType] = useState(
		initialData?.id ? "single" : "multiple",
	);

	useEffect(() => {
		if (!open) setFormData({});
	}, [open]);

	// Distinzione "altra rosa" attiva solo se conosciamo i giocatori della rosa della partita
	const hasRosterDistinction = rosterPlayerIds?.size > 0;
	const isOtherRoster = (p) =>
		hasRosterDistinction && !rosterPlayerIds.has(p.id);

	// Sorgente giocatori già filtrata a monte; marca chi è di un'altra rosa
	const formattedPlayers = (players ?? []).map((p) => {
		const opt = playerToOption(p);
		return isOtherRoster(p)
			? {...opt, label: `${opt.label} · Altra rosa`}
			: opt;
	});

	// Pre-fill when editing a single entry
	useEffect(() => {
		if (initialData?.id) {
			const player = players?.find((r) => r.id === initialData?.playerId);
			setFormData({
				player: player ? playerToOption(player) : null,
				rpe: initialData?.rpe,
			});
		}
	}, [initialData]);

	// Init multiple mode rows from convocations
	useEffect(() => {
		if (insertType === "multiple") {
			const playersValues = {};
			players?.forEach((p) => {
				playersValues[p.id] = {
					player: playerToOption(p),
				};
			});
			setFormData((f) => ({...f, players: playersValues}));
		}
	}, [insertType]);

	const updatePlayer = (pid, key, val) =>
		setFormData((f) => ({
			...f,
			players: {
				...f.players,
				[pid]: {...f.players?.[pid], [key]: val},
			},
		}));

	const requiredFields = insertType === "single" ? ["player", "rpe"] : [];

	const sortedPlayers = [...(players ?? [])].sort((a, b) => {
		if (hasRosterDistinction) {
			const ra = rosterPlayerIds.has(a.id) ? 0 : 1;
			const rb = rosterPlayerIds.has(b.id) ? 0 : 1;
			if (ra !== rb) return ra - rb;
		}
		return a.lastName.localeCompare(b.lastName);
	});

	return (
		<VLFModal
			maxWidth={insertType === "multiple" ? "md" : "sm"}
			open={open}
			title="Aggiungi informazione"
			close={close}
			confirm={() => {
				const errs = checkRequiredFields(requiredFields, formData);
				setErrors(errs);
				if (!errs.length) confirm(formData, insertType, initialData?.id);
			}}
			content={
				<div className="rpe-modal-wrap">
					{/* Single / Multiplo toggle */}
					{!initialData?.id && (
						<SwitchView
							currentSwitchView={insertType}
							setCurrentSwitchView={(value) => {
								setInsertType(value);
							}}
							pageOptions={[
								{value: "single", label: "Singolo"},
								{value: "multiple", label: "Multiplo"},
							]}
							fixed
							callback={() => setFormData({player: null})}
						/>
					)}

					{/* ── MULTIPLE mode ─────────────────────────────────── */}
					{insertType === "multiple" ? (
						<>
							{/* Column header */}
							<div className="rpe-multi-header">
								<span className="rpe-multi-header__name">Giocatore</span>
								<div className="rpe-multi-header__scale">
									{Array.from({length: 10}, (_, i) => i + 1).map((n) => (
										<span
											key={n}
											className={"rpe-multi-header__num " + rpeTone(n)}
										>
											{n}
										</span>
									))}
								</div>
							</div>

							{sortedPlayers.map((p) => {
								const row = formData?.players?.[p.id];
								return (
									<div key={p.id} className="rpe-multi-row">
										<span className="rpe-multi-row__name">
											{p.firstName} {p.lastName}
											{isOtherRoster(p) && (
												<span className="rpe-other-roster">Altra rosa</span>
											)}
										</span>
										<RpeSelector
											value={row?.rpe ?? ""}
											onChange={(val) =>
												updatePlayer(p.id, "rpe", {
													value: val,
													label: rpeOptions.find((o) => o.value === val)?.label,
												})
											}
										/>
									</div>
								);
							})}
						</>
					) : (
						// ── SINGLE mode ──────────────────────────────────
						<>
							<VLFSelectNew
								options={formattedPlayers}
								sortKey="sortKey"
								value={
									initialData?.id
										? formData?.player?.label
										: formData?.player || ""
								}
								onChange={(value) => {
									setErrors([]);
									setFormData({...formData, player: value});
								}}
								editable={!initialData?.id}
								required
								helperText={errors.includes("player") ? REQUIRED_FIELD : ""}
								error={errors.includes("player")}
								label="Giocatore"
							/>

							<div className="rpe-single-sel">
								<span
									className={`rpe-single-sel__label${errors.includes("rpe") ? " rpe-single-sel__label--error" : ""}`}
								>
									RPE <span className="rpe-required">*</span>
								</span>
								<RpeSelector
									value={formData.rpe?.value ?? formData.rpe ?? ""}
									onChange={(val) => {
										setErrors([]);
										setFormData({
											...formData,
											rpe: {
												value: val,
												label: rpeOptions.find((o) => o.value === val)?.label,
											},
										});
									}}
								/>
								{formData.rpe && (
									<span
										className={
											"rpe-single-sel__hint " +
											rpeTone(formData.rpe?.value ?? formData.rpe)
										}
									>
										{
											rpeOptions.find(
												(o) =>
													o.value === (formData.rpe?.value ?? formData.rpe),
											)?.label
										}
									</span>
								)}
								{errors.includes("rpe") && (
									<span className="rpe-error">{REQUIRED_FIELD}</span>
								)}
							</div>
						</>
					)}
				</div>
			}
		/>
	);
}

export default HandleRPEModal;
